import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isBefore, addMonths } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import api from '../../services/api';

const {
    FiDollarSign, FiTrendingUp, FiTrendingDown, FiCalendar,
    FiPieChart, FiAlertTriangle,
    FiCheck, FiTarget, FiActivity
} = FiIcons;

const Dashboard = () => {
    const { user } = useAuth();
    const [expenses, setExpenses] = useState([]);
    const [budget, setBudget] = useState(0);
    const [loading, setLoading] = useState(true);

    const userId = user?.id;

     useEffect(() => {
        const loadData = async () => {
            try {
                // const [expenseData, budgetData] = await Promise.all([
                //     api.get('expenses'),
                //     api.getBudget(userId),
                // ]);

                const [expenseData] = await Promise.all([
                    api.get('expenses'),
                ]);
                
                setExpenses(expenseData.data.data || []);
                setBudget(localStorage.getItem(`budget_${userId}`) || 0);
            } catch (error) {
                console.error('Failed to load dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [userId]);

    const normalizeToMonthly = (amount, frequency) => {
        const val = parseFloat(amount || 0);
        switch (frequency) {
            case 'Daily': return val * 30;
            case 'Weekly': return val * 4.33;
            case 'Bi-weekly': return val * 2.16;
            case 'Monthly': return val;
            case 'Quarterly': return val / 3;
            case 'Bi-Annually': return val / 6;
            case 'Annually': return val / 12;
            default: return val;
        }
    };

    const dashboardData = useMemo(() => {
        const today = new Date();

        // 1. Total Monthly normalized cost (This is the 'Total Expenses' from the tracker)
        const totalMonthlyExpenses = expenses.reduce((sum, exp) =>
            sum + normalizeToMonthly(exp.amount, exp.paymentFrequency), 0
        );

        // 2. Budget Calculations
        const utilization = budget > 0 ? (totalMonthlyExpenses / budget) * 100 : 0;
        const remainingBudget = budget - totalMonthlyExpenses; // Budget minus Total Expenses
        const isOverBudget = budget > 0 && totalMonthlyExpenses > budget;

        // 3. Upcoming Payments (next 30 days)
        const upcoming = expenses
            .map(exp => {
                const payDay = parseInt(exp.dayOfMonthPaidOn) || 1;
                let dueDate = new Date(today.getFullYear(), today.getMonth(), payDay);

                if (isBefore(dueDate, today)) {
                    dueDate = addMonths(dueDate, 1);
                }

                const daysLeft = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

                return {
                    ...exp,
                    dueDate: format(dueDate, 'MMM dd, yyyy'),
                    daysLeft,
                    status: daysLeft < 0 ? 'overdue' : (daysLeft < 3 ? 'upcoming' : 'pending')
                };
            })
            .sort((a, b) => a.daysLeft - b.daysLeft)
            .slice(0, 5);

        // 4. Recent Activity
        const recent = [...expenses]
            .filter(exp => exp.lastPaidDate)
            .sort((a, b) => new Date(b.lastPaidDate) - new Date(a.lastPaidDate))
            .slice(0, 5)
            .map(exp => ({
                ...exp,
                status: 'paid',
                date: format(new Date(exp.lastPaidDate), 'MMM dd, yyyy')
            }));

        return {
            totalMonthlyExpenses,
            budget,
            utilization,
            remainingBudget,
            isOverBudget,
            activeCount: expenses.length,
            upcoming,
            recent
        };
    }, [expenses, budget]);

    const stats = [
        {
            title: 'Estimated Monthly',
            value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dashboardData.totalMonthlyExpenses),
            subtitle: 'Total Expenses',
            icon: FiDollarSign,
            color: 'blue',
            trend: 'up'
        },
        {
            title: 'Monthly Budget',
            value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dashboardData.budget),
            subtitle: dashboardData.budget > 0 ? `${dashboardData.utilization.toFixed(1)}% Used` : 'Not Set',
            icon: FiTarget,
            color: 'purple',
            trend: dashboardData.isOverBudget ? 'down' : 'up'
        },
        {
            title: 'Remaining Budget',
            value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dashboardData.remainingBudget),
            subtitle: dashboardData.isOverBudget ? 'Over Limit' : 'Available',
            icon: FiPieChart,
            color: dashboardData.isOverBudget ? 'red' : 'green',
            trend: dashboardData.isOverBudget ? 'down' : 'up'
        },
        {
            title: 'Active Services',
            value: dashboardData.activeCount,
            subtitle: 'Tracked Expenses',
            icon: FiActivity,
            color: 'orange',
            trend: 'up'
        }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6"
                >
                    <div>
                        <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-2 uppercase tracking-tight">
                            Dashboard
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 font-medium uppercase tracking-widest text-xs">
                            Welcome back, {user?.name || user?.full_name} • {format(new Date(), 'MMMM yyyy')}
                        </p>
                    </div>

                    {dashboardData.budget > 0 && (
                        <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 min-w-[280px]">
                            <div className="flex justify-between items-center mb-3">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Monthly Spending Progress</span>
                                <span className={`text-xs font-black ${dashboardData.isOverBudget ? 'text-red-500' : 'text-primary-600'}`}>
                                    {dashboardData.utilization.toFixed(1)}%
                                </span>
                            </div>
                            <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${Math.min(dashboardData.utilization, 100)}%` }}
                                    className={`h-full rounded-full transition-all duration-1000 ${dashboardData.isOverBudget ? 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]' : 'bg-primary-600 shadow-[0_0_10px_rgba(37,99,235,0.3)]'}`}
                                />
                            </div>
                            {dashboardData.isOverBudget && (
                                <div className="mt-2 flex items-center text-red-500 text-[10px] font-bold uppercase tracking-tighter">
                                    <SafeIcon icon={FiAlertTriangle} className="mr-1 w-3 h-3" />
                                    Budget limit exceeded by {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(dashboardData.remainingBudget))}
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                    {stats.map((stat, index) => (
                        <motion.div
                            key={stat.title}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 relative overflow-hidden group"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 duration-300 ${stat.color === 'blue' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' :
                                        stat.color === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400' :
                                            stat.color === 'green' ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' :
                                                stat.color === 'red' ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' :
                                                    'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                                    }`}>
                                    <SafeIcon icon={stat.icon} className="w-6 h-6" />
                                </div>
                                <div className={`flex items-center ${stat.trend === 'up' && stat.color !== 'red' ? 'text-green-500' : 'text-red-500'
                                    }`}>
                                    <SafeIcon icon={stat.trend === 'up' ? FiTrendingUp : FiTrendingDown} className="w-4 h-4 mr-1" />
                                </div>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-1 tracking-tight">
                                {stat.value}
                            </h3>
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest flex items-center justify-between">
                                <span>{stat.title}</span>
                                <span className={stat.color === 'red' ? 'text-red-500' : 'text-gray-500'}>{stat.subtitle}</span>
                            </p>
                        </motion.div>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Recent Payments Section */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                            <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest flex items-center">
                                <SafeIcon icon={FiCheck} className="mr-2 text-green-500 w-4 h-4" />
                                Recent Payments
                            </h2>
                            <Link to="/expense-tracker" className="text-[10px] font-black text-primary-600 uppercase tracking-widest hover:underline">
                                View All
                            </Link>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                {dashboardData.recent.map((item) => (
                                    <div key={item.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all border border-transparent hover:border-gray-100 dark:hover:border-gray-600">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center text-green-600">
                                                <SafeIcon icon={FiCheck} className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-bold text-gray-900 dark:text-white">{item.service}</h4>
                                                <p className="text-[10px] text-gray-400 font-medium uppercase">{item.date}</p>
                                            </div>
                                        </div>
                                        <span className="text-sm font-black text-gray-900 dark:text-white">
                                            ${parseFloat(item.amount).toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                                {dashboardData.recent.length === 0 && (
                                    <div className="text-center py-10">
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">No recent payments recorded</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>

                    {/* Upcoming Obligations Section */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                        <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                            <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest flex items-center">
                                <SafeIcon icon={FiCalendar} className="mr-2 text-primary-500 w-4 h-4" />
                                Upcoming Obligations
                            </h2>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                {dashboardData.upcoming.map((item) => (
                                    <div key={item.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all border border-transparent hover:border-gray-100 dark:hover:border-gray-600">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.daysLeft <= 3 ? 'bg-red-50 text-red-600 dark:bg-red-900/20' : 'bg-blue-50 text-blue-600 dark:bg-blue-900/20'
                                                }`}>
                                                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-bold text-gray-900 dark:text-white">{item.service}</h4>
                                                <p className="text-[10px] text-gray-400 font-medium uppercase">Due {item.dueDate}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-black text-gray-900 dark:text-white">${parseFloat(item.amount).toFixed(2)}</p>
                                            <p className={`text-[9px] font-black uppercase tracking-tighter ${item.daysLeft <= 3 ? 'text-red-500' : 'text-gray-400'}`}>
                                                {item.daysLeft} days remaining
                                            </p>
                                        </div>
                                    </div>
                                ))}
                                {dashboardData.upcoming.length === 0 && (
                                    <div className="text-center py-10">
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">No upcoming obligations</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Action Banner */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-10 bg-black dark:bg-white rounded-[2rem] p-10 text-white dark:text-black relative overflow-hidden group"
                >
                    <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600/20 rounded-full -mr-48 -mt-48 blur-[100px] group-hover:bg-primary-600/30 transition-all duration-700"></div>
                    <div className="relative z-10 flex flex-col items-center text-center max-w-3xl mx-auto">
                        <h2 className="text-4xl font-black mb-4 uppercase tracking-tighter leading-none">
                            Financial Control Excellence
                        </h2>
                        <p className="text-gray-400 dark:text-gray-500 mb-8 font-medium text-lg leading-relaxed">
                            Your "Estimated Monthly" cost is currently <span className="text-white dark:text-black font-black">${dashboardData.totalMonthlyExpenses.toFixed(2)}</span>.
                            {dashboardData.isOverBudget
                                ? " You have exceeded your budget. Review your active services to optimize spending."
                                : " You are within your budget limits. Great job managing your resources!"}
                        </p>
                        <Link
                            to="/expense-tracker"
                            className="px-10 py-4 bg-primary-600 text-white font-black rounded-2xl hover:bg-primary-700 transition-all shadow-2xl hover:shadow-primary-600/40 transform hover:-translate-y-1 uppercase tracking-widest text-xs"
                        >
                            Manage Expenses
                        </Link>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Dashboard;